/* v2.87 · Shared primitives for the calculator cards.
   Extracted from Calculators.jsx during the components/calculators/
   refactor to mirror the successful components/batch/ cleanup.

   `F` and `StaleResultPlaceholder` were hoisted to module scope in v2.64
   to keep input focus stable across re-renders. Do NOT redefine them
   inside component bodies — inputs will lose focus after one keystroke. */

export const F = ({ label, children }) => (
  <label className="block">
    <span className="label-tag block mb-1">{label}</span>
    {children}
  </label>
);

export const StaleResultPlaceholder = ({ tone = "cyan", testid }) => {
  const border = tone === "cyan" ? "border-[#00E5FF]/20" : "border-[#FFCC00]/20";
  const text = tone === "cyan" ? "text-[#00E5FF]/60" : "text-[#FFCC00]/70";
  return (
    <div
      className={`mt-6 border ${border} bg-[#0A0C10]/60 p-4 font-mono text-sm min-h-[220px] flex items-center justify-center`}
      data-testid={testid}
    >
      <div className={`text-center ${text}`}>
        <div className="label-tag">// results cleared</div>
        <div className="mt-2 text-xs">Inputs changed — click <span className="font-bold">Compute</span> to refresh.</div>
      </div>
    </div>
  );
};

export function Stat({ label, value, accent, warn, ...rest }) {
  return (
    <div {...rest}>
      <div className="label-tag">{label}</div>
      <div className={`mt-1 text-base ${accent ? "text-[#00E5FF]" : warn ? "text-[#FF3B30]" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

/* v2.83 · Compact per-side cable summary card used by the transformer
   both-sides result panel. */
export function TxCableCard({ side, cable, fla, ocpd_pct }) {
  const tone = side === "primary" ? "cyan" : "yellow";
  const borderCls = tone === "cyan" ? "border-[#00E5FF]/40" : "border-[#FFCC00]/40";
  const textCls = tone === "cyan" ? "text-[#00E5FF]" : "text-[#FFCC00]";
  return (
    <div className={`border ${borderCls} bg-black/40 p-3`}
         data-testid={`cond-tx-${side}-cable`}>
      <div className={`label-tag ${textCls}`}>// {side} cable @ {cable.voltage} V · {cable.length_ft} ft</div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs font-mono">
        <div>
          <div className="text-[9px] uppercase text-gray-500">Selected size</div>
          <div className={`text-lg font-bold ${textCls}`}>{cable.selected_size} <span className="text-[10px] text-gray-400">AWG/kcmil</span></div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-gray-500">Derated ampacity</div>
          <div className="text-lg font-bold text-white">{cable.derated_ampacity} A</div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-gray-500">OCPD ({ocpd_pct}%)</div>
          <div className={`text-lg font-bold ${textCls}`}>{cable.recommended_ocpd_amps} A</div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-gray-500">Voltage drop</div>
          <div className={`text-lg font-bold ${cable.voltage_drop_compliant ? "text-white" : "text-[#FF8A85]"}`}>{cable.voltage_drop_pct}%</div>
        </div>
      </div>
      <div className="mt-2 text-[10px] text-gray-500">
        FLA {fla} A · sized to 125 % (NEC 450.3 &amp; 240.21(C)) · {cable.selection_driven_by === "voltage_drop" ? "VD-driven" : cable.selection_driven_by === "breaker_protection" ? "OCPD-driven" : "ampacity-driven"}
      </div>
    </div>
  );
}
